import type { CaravanDb } from '../data/local/db'
import { OutboxRepository } from '../data/local/outboxRepository'
import type { RemoteSyncGateway } from '../data/remote/gateway'
import {
  AuthorizationSyncError,
  PULL_PAGE_SIZE,
  TransientSyncError,
  entityKey,
  sanitizedSyncMessage,
  type PullChange,
  type SyncDiagnostic,
} from '../domain/sync'

type SyncEngineOptions = {
  now?: () => number
  random?: () => number
  onRevoked?: () => Promise<void> | void
  mode?: 'demo' | 'supabase'
}

export class SyncEngine {
  readonly #db: CaravanDb
  readonly #gateway: RemoteSyncGateway
  readonly #outbox: OutboxRepository
  readonly #now: () => number
  readonly #random: () => number
  readonly #onRevoked: () => Promise<void> | void
  readonly #mode: 'demo' | 'supabase'
  #running: Promise<void> | null = null

  constructor(db: CaravanDb, gateway: RemoteSyncGateway, options: SyncEngineOptions = {}) {
    this.#db = db
    this.#gateway = gateway
    this.#outbox = new OutboxRepository(db)
    this.#now = options.now ?? Date.now
    this.#random = options.random ?? Math.random
    this.#onRevoked = options.onRevoked ?? (() => undefined)
    this.#mode = options.mode ?? 'demo'
  }

  recoverInterruptedWork(): Promise<number> {
    return this.#outbox.recoverInterruptedWork()
  }

  syncOnce(): Promise<void> {
    this.#running ??= this.#performSync().finally(() => {
      this.#running = null
    })
    return this.#running
  }

  async retryNow(): Promise<void> {
    await this.#outbox.retryActionable()
    await this.syncOnce()
  }

  async #performSync(): Promise<void> {
    const startedAt = this.#now()
    const batch = await this.#outbox.takeDueBatch(startedAt)
    if (batch.length > 0) {
      try {
        const receipts = await this.#gateway.pushMutations(batch)
        await this.#outbox.applyReceipts(receipts)
        await this.#diagnose('push', 'success', batch[0]?.entityType ?? null, batch.length, startedAt)
      } catch (error) {
        if (error instanceof AuthorizationSyncError) {
          await this.#db.outbox.where('state').equals('syncing').modify({ state: 'recovery' })
          await this.#onRevoked()
          await this.#diagnose('push', 'revoked', null, batch.length, startedAt, error.message)
          return
        }
        const transient = error instanceof TransientSyncError
        await this.#outbox.reschedule(
          batch,
          this.#now(),
          this.#random,
          error instanceof Error ? error.message : error,
          transient ? error.retryAfterMs : null,
        )
        await this.#diagnose('push', 'retry', null, batch.length, startedAt, error)
        return
      }
    }
    try {
      await this.#pullAll(startedAt)
    } catch (error) {
      if (error instanceof AuthorizationSyncError) {
        await this.#onRevoked()
        await this.#diagnose('pull', 'revoked', null, 0, startedAt, error.message)
        return
      }
      await this.#diagnose('pull', 'retry', null, 0, startedAt, error)
    }
  }

  async #pullAll(startedAt: number): Promise<void> {
    const setting = await this.#db.settings.get('sync')
    let cursor = setting?.mode === this.#mode ? setting.lastCursor : 0
    let hasMore = true
    while (hasMore) {
      const page = await this.#gateway.pullChanges(cursor, PULL_PAGE_SIZE)
      await this.#db.transaction('rw', this.#db.entities, this.#db.settings, async () => {
        for (const change of page.changes) await this.#applyChange(change)
        cursor = page.nextCursor
        await this.#db.settings.put({
          key: 'sync',
          schemaRevision: 1,
          mode: this.#mode,
          lastCursor: cursor,
          lastSuccessfulSyncAt: new Date(this.#now()).toISOString(),
          lastPendingAlertAt: setting?.lastPendingAlertAt ?? null,
          protectedCacheState: setting?.protectedCacheState ?? 'open',
        })
      })
      hasMore = page.hasMore
    }
    await this.#diagnose('pull', 'success', null, 0, startedAt)
  }

  async #applyChange(change: PullChange): Promise<void> {
    const key = entityKey(change.entityType, change.entityId)
    const local = await this.#db.entities.get(key)
    if (local && ['pending', 'syncing', 'failed', 'conflict', 'recovery'].includes(local.syncState)) {
      return
    }
    await this.#db.entities.put({
      key,
      id: change.entityId,
      tripId: change.tripId,
      entityType: change.entityType,
      payload: structuredClone(change.payload),
      createdAtDevice: local?.createdAtDevice ?? change.updatedAtServer ?? new Date(this.#now()).toISOString(),
      updatedAtDevice: local?.updatedAtDevice ?? change.updatedAtServer ?? new Date(this.#now()).toISOString(),
      updatedAtServer: change.updatedAtServer ?? new Date(this.#now()).toISOString(),
      version: change.version,
      deletedAt: change.deletedAt,
      lastMutationId: local?.lastMutationId ?? `remote:${String(change.cursor)}`,
      syncState: 'synced',
      lastError: null,
    })
  }

  async #diagnose(
    phase: SyncDiagnostic['phase'],
    outcome: string,
    entityType: SyncDiagnostic['entityType'],
    attemptCount: number,
    startedAt: number,
    message?: unknown,
  ): Promise<void> {
    const diagnostic: SyncDiagnostic = {
      id: crypto.randomUUID(),
      createdAt: new Date(this.#now()).toISOString(),
      phase,
      entityType,
      outcome,
      correlationId: crypto.randomUUID(),
      attemptCount,
      durationMs: Math.max(0, this.#now() - startedAt),
      message: sanitizedSyncMessage(message),
    }
    await this.#db.transaction('rw', this.#db.diagnostics, async () => {
      await this.#db.diagnostics.put(diagnostic)
      const excess = (await this.#db.diagnostics.orderBy('createdAt').primaryKeys()).slice(0, -200)
      if (excess.length > 0) await this.#db.diagnostics.bulkDelete(excess)
    })
  }
}
